// @flow

import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { getRouteParams, getCurrentPath } from "@selectors";
import { route } from "@routes/helper";
import { formatAuthoredBy } from "@transformers/names";
import { Loader, View, Text, Button, RightPanel } from "@components";
import {
  getEmployee,
  getEmployeeName,
  isLoading,
  getUserRecentLogins,
  getScheduledReportLogDataModalData,
  getScheduledReportLogDataModalVisible,
  getAccessLevelValue,
} from "@modules/employee/selectors";
import actionCreators from "@modules/employee/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import {
  UserAccountDetailsGeneralInfo,
  RecentLogins,
  SavedReports,
  UserAccountAreaOfAccess,
  UserAccountSystemLogins,
  ScheduledReportLogModal,
  HelpPanelUserAccount,
} from "@modules/employee/components";
import type { EmployeeType } from "@modules/employee/types";
import withErrorHandler from "@components/error";
import { withClientContext, type PropsFromClientContext } from "@modules/context/components/";
import {
  withPermissions,
  PermissionEnum,
  type PropsFromPermissions,
} from "@modules/account/components";
import type { LoginRecord } from "@modules/employee/reducer/user-account-details/types";

import type { ScheduledReportLogData } from "@modules/employee/reducer/user-account-details/types";

type PropsFromState = $Exact<{
  currentPath: string,
  isLoading: boolean,
  employeeId: number,
  employee: EmployeeType,
  recentLogins: Array<LoginRecord>,
  reportLogData: ScheduledReportLogData,
  reportLogDataModalVisible: boolean,
  accessLevelValue: ?Object,
}>;

type PropsFromDispatch = $Exact<{
  getEmployees: typeof actionCreators.getEmployees,
  getRecentLogins: typeof actionCreators.GetUserRecentLogins,
  getHierarchyTree: typeof sharedActionCreators.GetHierarchyTree,
  getUserAccountAssignments: typeof actionCreators.GetUserAccountAssignments,
}>;

type Props = $Exact<{
  ...PropsFromPermissions,
  ...PropsFromClientContext,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type OwnState = $Exact<{
  isPanelOpened: boolean,
}>;

const restrictedAccessUserTypes = ["MANAGER", "EMPLOYEE"];

export function EmployeeAccountPage(props: Props) {
  const [isPanelOpened, setIsPanelOpened] = useState(false);

  const { clientId, employeeId } = props;

  useEffect(() => {
    props.getEmployees({
      clientId: props.clientId,
      filters: {
        id: props.employeeId,
      },
    });

    props.getRecentLogins({
      userId: props.employeeId,
    });

    props.getHierarchyTree({
      clientId: props.clientId,
    });

    props.getUserAccountAssignments({
      clientId: props.clientId,
      employeeId: props.employeeId,
    });
  }, [clientId, employeeId]);

  function toggleRightPanel() {
    setIsPanelOpened(prev => !prev);
  }

  const shouldShowAreaOfAccess =
    props.accessLevelValue && !restrictedAccessUserTypes.includes(props.accessLevelValue.userType);

  return (
    <View position="relative">
      {props.isLoading && <Loader inline />}
      <View direction="row">
        <View flex="1"></View>
        <Button className="ml-3" onClick={toggleRightPanel} materialIcon="help">
          {props.t("HELP")}
        </Button>
      </View>
      {isPanelOpened && (
        <RightPanel title={props.t("ACCOUNT")} onClose={toggleRightPanel}>
          <HelpPanelUserAccount />
        </RightPanel>
      )}
      <View gap={20} flex={1} margin={!shouldShowAreaOfAccess ? "0 0 2rem" : ""}>
        <UserAccountDetailsGeneralInfo
          clientId={props.clientId}
          employeeId={props.employeeId}
          employee={props.employee}
        />
        {shouldShowAreaOfAccess && (
          <UserAccountAreaOfAccess
            clientId={props.clientId}
            employeeId={props.employeeId}
            employee={props.employee}
          />
        )}
      </View>
      <RecentLogins data={props.recentLogins} />
    </View>
  );
}

const mapStateToProps = (state: Object, ownProps): PropsFromState => {
  const routeParams = getRouteParams(ownProps);
  const employeeId = parseInt(routeParams.employeeId);

  return {
    isLoading: isLoading(state),
    currentPath: getCurrentPath(state),
    employee: getEmployee(employeeId)(state),
    employeeId,
    recentLogins: getUserRecentLogins(state),
    reportLogData: getScheduledReportLogDataModalData(state),
    reportLogDataModalVisible: getScheduledReportLogDataModalVisible(state),
    accessLevelValue: getAccessLevelValue(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getEmployees: actionCreators.getEmployees,
  getRecentLogins: actionCreators.GetUserRecentLogins,
  getHierarchyTree: sharedActionCreators.GetHierarchyTree,
  getUserAccountAssignments: actionCreators.GetUserAccountAssignments,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(EmployeeAccountPage));
